import { reactive } from "vue";
import App from "@bloglovin/inspire-engine/App";
import { PLATFORM_ENTITY_APP_NAME, isBuilderAppBooted }
  from "%/dynamic-component-editor/classes/platform-standard-entities";

// Stable canvas keys across the optimistic temp-PK -> server real-PK swap.
//
// The canvas renders children with `v-for childId :key="childId"`. `childId`
// is the element's pk, so when the engine reconciles an optimistically-created
// element's temp pk to its real pk, the key flips (e.g. "1782519028816" ->
// "553") and Vue unmounts the whole temp-keyed subtree and mounts a fresh
// real-keyed one — a full double mount per insert. We alias the real id back to
// the temp id it replaced so the key stays constant and Vue reuses the
// component, updating `:identifier` in place.
//
// The alias MUST be recorded synchronously, the instant the reconcile swaps
// temp->real in `mountedChildIds`, and BEFORE the v-for re-renders with the
// real id — otherwise the child renders once under the real key (remount) and
// the late alias would flip it again (a second remount). The engine's
// frame-budget yielding decouples its TEMP_PK_RESOLVED_EVENT from the render,
// so we don't rely on that event: `recordChildKeyAliases` is called from the
// reconcile itself (a watcher callback — runs untracked, so this introduces no
// reactive dependency) and resolves temp->real directly. The map is reactive
// so `stableChildKey` re-reads pick up new aliases.
const realToTempKey = reactive({});

// Record real->temp key aliases for any id being removed this reconcile that is
// a temp pk reconciled to a real pk now being added. `addedSet` is a Set of the
// bare ids being mounted; `removedIds` the bare ids leaving.
export function recordChildKeyAliases(removedIds, addedSet) {
  if (!removedIds.length || addedSet.size === 0) {
    return 0;
  }
  // Builder-only reconcile (called from the builder canvas element). Capability-check
  // the builder app rather than try/catch a render-surface throw.
  if (!isBuilderAppBooted()) {
    return 0;
  }
  const app = App.get(PLATFORM_ENTITY_APP_NAME);
  let aliased = 0;
  for (const removed of removedIds) {
    const bareTemp = String(removed);
    const scalar = Number(bareTemp);
    if (!Number.isFinite(scalar)) {
      continue;
    }
    let real;
    try {
      real = app.resolveTempPk("Element", { id: scalar });
    } catch (_e) {
      continue;
    }
    if (!real) {
      continue;
    }
    const bareReal = String(real.id ?? Object.values(real)[0] ?? "");
    if (bareReal && addedSet.has(bareReal)) {
      realToTempKey[bareReal] = realToTempKey[bareTemp] ?? bareTemp;
      aliased += 1;
    }
  }
  return aliased;
}

// Returns a key that survives the temp->real pk swap: the original temp id when
// `childId` is a reconciled real id, otherwise `childId` unchanged.
export function stableChildKey(childId) {
  const bare = String(childId);
  return realToTempKey[bare] ?? bare;
}

export function recordKeyAlias(realId, tempId) {
  const bareReal = String(realId);
  const bareTemp = String(tempId);
  if (bareReal === bareTemp) {
    return;
  }
  realToTempKey[bareReal] = realToTempKey[bareTemp] ?? bareTemp;
}

export function stableSelectionKey(identifier) {
  const [bareId, ...alias] = String(identifier).split(":");
  return [stableChildKey(bareId), ...alias].join(":");
}
